import {useFetcher, useMatches, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import type {FunctionComponent} from 'react'
import type {RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {revokeAllConsent, grantAllConsent} from '~/utils/consent-manager.client'
import type {CookieConsentProps} from '.'

const OptInOutCookieConsent: FunctionComponent<CookieConsentProps> = ({
  showOnBottom,
  showCookieConsentGdpr,
  setShowCookieConsentGdpr,
}) => {
  const {language, featureFlags, deviceType, domain, locale, cookieConsentVariation, trackAnalytics} =
    useRouteLoaderData('root') as RootLoaderResponse
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const translations = getTranslation(language)
  const analyticsFetcher = useFetcher()
  const location = useMatches()
  const isErrorPage = Boolean(location.some(({id}) => id === 'routes/404'))
  const newCookieUi = featureFlags?.showNewHeaderAndCookieUi && deviceType && deviceType !== 'desktop'

  return (
    showCookieConsentGdpr && (
      <div
        data-testid="cookie-consent"
        className={clsx(
          'w-full bg-white',
          newCookieUi ? 'mobile:h-24' : 'mobile:h-16',
          isErrorPage ? '' : 'h-[62px]',
          'z-50',
          showOnBottom ? 'fixed bottom-0 mobile:h-min' : {sticky: true, 'top-12': newCookieUi, 'top-16': !newCookieUi},
        )}
      >
        <div
          className={
            !showOnBottom
              ? 'absolute top-0 flex w-full justify-center border border-b-neutral-200 bg-white px-4 py-4 mobile:pb-3 mobile:pt-2'
              : 'flex w-full justify-center border border-b-neutral-200 bg-white py-2 mobile:py-1'
          }
        >
          <div
            className={clsx(
              'utils_main_container flex w-[97%] flex-1 flex-row justify-between',
              stylesBydomain.UTILS_MAIN_CONTAINER,
            )}
          >
            <div className="flex flex-col justify-center gap-1 pr-4">
              <span className="text-sm font-bold text-black">{translations.CookieConsent.title}</span>
              <span
                className="text-[10px] leading-3 text-neutral-700 [&>a]:underline"
                dangerouslySetInnerHTML={{__html: translations.CookieConsent.newConsentText}}
              />
            </div>
            <analyticsFetcher.Form
              className="flex items-center gap-4 mobile:flex-col-reverse mobile:justify-center mobile:gap-1"
              method="post"
              action="/cookie-consent"
              onSubmit={() => {
                const value = (document.activeElement as HTMLButtonElement).value
                if (typeof window !== 'undefined') {
                  if (value === 'denied') {
                    revokeAllConsent()
                  } else {
                    grantAllConsent()
                  }
                  window.gtag?.('consent', 'update', {
                    ad_user_data: value,
                    ad_personalization: value,
                    ad_storage: value,
                    analytics_storage: value,
                  })
                }
                setShowCookieConsentGdpr(false)
              }}
            >
              <input
                type="hidden"
                name={cookieConsentVariation === 'opt-in' ? 'track-analytics-data' : 'cookieConsent'}
                value={cookieConsentVariation === 'opt-in' ? JSON.stringify(trackAnalytics) : 'hidden'}
              />
              <button
                className="h-9 rounded-3xl border border-black bg-white px-6 text-sm font-semibold text-black hover:bg-[#E8E8E8] mobile:h-8 mobile:w-full"
                name="accept-gdpr"
                value="denied"
                type="submit"
                data-testid="reject-button"
              >
                {translations.CookieConsent.rejectButton}
              </button>
              <button
                className="h-9 rounded-3xl border border-black bg-neutral-700 px-7 text-sm font-semibold text-white hover:bg-[#828282] mobile:h-8 mobile:w-full"
                name="accept-gdpr"
                value="granted"
                type="submit"
                data-testid="accept-button"
              >
                {translations.CookieConsent.acceptButton}
              </button>
            </analyticsFetcher.Form>
          </div>
        </div>
      </div>
    )
  )
}

export default OptInOutCookieConsent
